// MCP Security — Overview page.
//
// Rebuilt 2026-10-11 to mirror the Claude Design mockup
// (mcp-security-app.html / dc_sections/OVERVIEW.html):
//   - endpoint filter + severity-tab row
//   - 8-card metric strip (sampled from CP /overview + /events)
//   - hourly decisions stacked bar chart (pass / tag / block)
//   - tool-call origin by GeoIP + attack-class mix pie
//   - 6-grid Top-N panels (tools, endpoints, IPs, attack-classes,
//     agents, sessions)
//   - "MCP-only signals" strip with 3 anomaly cards
//   - Drift queue row (empty-state friendly)
//   - Token spend 14-day stacked bar
//
// Real data wiring:
//   GET /mcp/sites/:site_uid/overview?hours=24
//     → risk, endpoints, rollup, per_endpoint,
//       geo, token_spend, anomalies, drift, unique_agents, model_mix
//   GET /mcp/sites/:site_uid/events?hours=24&limit=1000
//
// Every panel below reads from one of those two responses — no stub /
// placeholder data remains on this page.

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Alert, Button, Skeleton } from "@mui/material";

import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import { GreenTimeRangePicker } from "../../../../common/Greens";

import axios from "../../../../../utils/axios/v1/userAxios";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";

import tokens from "./_shared/tokens";
import MetricCard from "./_shared/MetricCard";
import TopPanel from "./_shared/TopPanel";
import CountryFlag from "./_shared/CountryFlag";
import { ATTACK_CLASS_LABEL } from "./_shared/AttackChips";
import {
  primaryButtonStyle,
  sectionCardSx,
  sectionCardTitleSx,
} from "./_shared/buttonStyles";

// Full English name per ISO-2 country code (hand-picked subset — the
// rest render as "<code>"). Keeps the grid readable without pulling in
// an i18n lib. Extend as traffic needs demand.
const COUNTRY_NAME = {
  US: "United States", GB: "United Kingdom", DE: "Germany", FR: "France",
  JP: "Japan", CA: "Canada", AU: "Australia", BR: "Brazil", IN: "India",
  IT: "Italy", ES: "Spain", NL: "Netherlands", SE: "Sweden", NO: "Norway",
  FI: "Finland", DK: "Denmark", IE: "Ireland", PL: "Poland", CH: "Switzerland",
  AT: "Austria", BE: "Belgium", RU: "Russia", CN: "China", KR: "South Korea",
  SG: "Singapore", AE: "UAE", IL: "Israel", TR: "Turkey", MX: "Mexico",
  AR: "Argentina", ZA: "South Africa", NG: "Nigeria", EG: "Egypt",
  SA: "Saudi Arabia", ID: "Indonesia", MY: "Malaysia", PH: "Philippines",
  TH: "Thailand", VN: "Vietnam", HK: "Hong Kong", TW: "Taiwan", NZ: "New Zealand",
};

// ─────────────────────────────────────────────────────────────────────
// Attack-family bucketing (same as the CH event drawer).
// ─────────────────────────────────────────────────────────────────────
const FAMILY_LABEL = {
  model_template: "Model-template tokens",
  override_english: "English overrides",
  override_multiling: "Multilingual overrides",
  stego_unicode: "Stego / invisible unicode",
  role_transition: "Role-transition",
  literal_sig: "MCP literal signature",
  injection_other: "Other injection",
  credential: "Credential leak",
  exfil: "Exfil (link / blob)",
  taint: "Taint (confused-deputy)",
  anomaly: "Session anomaly",
  schema: "Schema violation",
  resource_uri: "URI blocked",
  agent: "Agent denied",
};
const FAMILY_ORDER = [
  "model_template", "override_english", "override_multiling", "stego_unicode",
  "role_transition", "literal_sig", "injection_other",
  "credential", "exfil", "taint", "anomaly", "schema", "resource_uri", "agent",
];

function attackFamily(scanner, slug) {
  if (scanner === "credential") return "credential";
  if (scanner === "exfil") return "exfil";
  if (scanner === "taint") return "taint";
  if (scanner === "anomaly") return "anomaly";
  if (scanner === "schema") return "schema";
  if (scanner === "resource_uri") return "resource_uri";
  if (scanner === "agent") return "agent";
  if (scanner === "sig") return "literal_sig";
  if (!slug) return "injection_other";
  if (slug.startsWith("override_i18n_")) return "override_multiling";
  if (slug.startsWith("override_")) return "override_english";
  if (slug.startsWith("gemini_") || slug.startsWith("deepseek_") ||
      slug.startsWith("mistral_") || slug.startsWith("llama_") ||
      slug.startsWith("chatml_") || slug === "openai_endoftext") return "model_template";
  if (slug.startsWith("zero_width_") || slug === "bom" || slug === "tag_character" ||
      slug.startsWith("variation_") || slug.startsWith("bidi_")) return "stego_unicode";
  if (slug === "role_transition") return "role_transition";
  return "injection_other";
}

const hoursForPeriod = (p) => (p === "1h" ? 1 : p === "7d" ? 168 : p === "30d" ? 720 : 24);

const SEV_OPTS = [
  { k: "all", label: "All" },
  { k: "block", label: "Blocked only" },
  { k: "tag", label: "Tagged" },
  { k: "pass", label: "Pass + suppress" },
];

const COLORS = {
  pass: "#4caf7a",
  tag: "#e0a140",
  block: "#e85c5c",
};

// Palette for pie slices — enough for 14 attack-class families.
const PIE_COLORS = [
  "#6371F7", "#F2B429", "#1FC16B", "#FB3748", "#3577F1", "#F5C354",
  "#5B2D8E", "#1A4FBF", "#8A6410", "#8E1F1F", "#C29021", "#2BA4C7",
  "#B55DAE", "#4FA78A",
];

// ─────────────────────────────────────────────────────────────────────
// Hourly decisions chart. Buckets events into 72 1-h slots stacked
// pass / tag / block. SVG-free: pure flex bars.
// ─────────────────────────────────────────────────────────────────────
function HourlyChart({ events }) {
  const buckets = useMemo(() => {
    const n = 72;
    const now = Date.now();
    const start = now - n * 3600 * 1000;
    const out = Array.from({ length: n }, () => ({ pass: 0, tag: 0, block: 0 }));
    for (const r of events || []) {
      const t = new Date(r.ts).getTime();
      if (isNaN(t) || t < start) continue;
      const slot = Math.min(n - 1, Math.max(0, Math.floor((t - start) / 3600000)));
      const o = r.outcome;
      const bucket = (o === "pass" || o === "suppress") ? "pass" : o === "tag" ? "tag" : "block";
      out[slot][bucket] += 1;
    }
    return out;
  }, [events]);
  const max = Math.max(1, ...buckets.map((b) => b.pass + b.tag + b.block));
  const yTicks = [max, Math.round(max / 2), 0];
  return (
    <Box sx={{ ...sectionCardSx, p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={sectionCardTitleSx}>Hourly decisions</Typography>
          <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
            Last 72 h. Each bar is 1 hour, stacked pass / tag / block.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1.5}>
          <LegendDot colour={COLORS.pass} label="pass + suppress" />
          <LegendDot colour={COLORS.tag} label="tag" />
          <LegendDot colour={COLORS.block} label="block + hold" />
        </Stack>
      </Stack>
      <Box sx={{ display: "flex", gap: 1.25 }}>
        <Stack justifyContent="space-between" sx={{ fontSize: 11, color: tokens.neutral[600], fontFamily: tokens.fontMono, height: 200, textAlign: "right", width: 36 }}>
          {yTicks.map((y) => <span key={y}>{y}</span>)}
        </Stack>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              height: 200, display: "flex", alignItems: "flex-end", gap: "2px",
              borderBottom: `1px solid ${tokens.neutral[300]}`,
              backgroundImage: `linear-gradient(${tokens.neutral[150]} 1px, transparent 1px)`,
              backgroundSize: "100% 50px",
            }}
          >
            {buckets.map((b, i) => {
              const h = (v) => `${(v / max) * 100}%`;
              return (
                <Box key={i} sx={{ flex: 1, minWidth: 0, height: "100%", display: "flex", flexDirection: "column-reverse" }}>
                  <Box title={`pass: ${b.pass}`} sx={{ height: h(b.pass), background: COLORS.pass }} />
                  <Box title={`tag: ${b.tag}`} sx={{ height: h(b.tag), background: COLORS.tag }} />
                  <Box title={`block: ${b.block}`} sx={{ height: h(b.block), background: COLORS.block }} />
                </Box>
              );
            })}
          </Box>
          <Stack direction="row" justifyContent="space-between" sx={{ pt: 0.75, fontSize: 11, fontFamily: tokens.fontMono, color: tokens.neutral[600] }}>
            <span>72h ago</span><span>48h</span><span>24h</span><span>now</span>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
}

function LegendDot({ colour, label }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, color: tokens.neutral[800] }}>
      <Box sx={{ width: 10, height: 10, borderRadius: "2px", background: colour }} />
      <span>{label}</span>
    </Stack>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Attack-class mix as a conic-gradient donut.
// ─────────────────────────────────────────────────────────────────────
function AttackMixDonut({ familyCounts }) {
  const total = Object.values(familyCounts).reduce((a, v) => a + v, 0);
  const entries = FAMILY_ORDER
    .filter((k) => familyCounts[k])
    .map((k, i) => ({ k, label: FAMILY_LABEL[k], v: familyCounts[k], color: PIE_COLORS[i % PIE_COLORS.length] }));
  let acc = 0;
  const gradient = total === 0
    ? tokens.neutral[100]
    : "conic-gradient(" +
      entries.map((e) => {
        const start = (acc / total) * 360;
        acc += e.v;
        const end = (acc / total) * 360;
        return `${e.color} ${start.toFixed(2)}deg ${end.toFixed(2)}deg`;
      }).join(",") +
      ")";
  return (
    <Box sx={{ ...sectionCardSx, p: 2.5 }}>
      <Typography sx={sectionCardTitleSx}>Attack-class mix</Typography>
      <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 1.5 }}>
        All phase-A families seen in this window.
      </Typography>
      <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
        <Box sx={{ width: 168, height: 168, borderRadius: "50%", display: "grid", placeItems: "center", background: gradient }}>
          <Box sx={{ width: 104, height: 104, borderRadius: "50%", background: "#fff", display: "grid", placeItems: "center" }}>
            <Box sx={{ textAlign: "center" }}>
              <Typography sx={{ fontSize: 22, fontWeight: 600 }}>{total.toLocaleString()}</Typography>
              <Typography sx={{ fontSize: 11, color: tokens.neutral[700] }}>signal hits</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 220, display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 16px" }}>
          {entries.length === 0 && (
            <Typography sx={{ gridColumn: "1/-1", fontSize: 12, color: tokens.neutral[700] }}>
              A quiet window — no attack-class hits recorded.
            </Typography>
          )}
          {entries.map((e) => (
            <Stack key={e.k} direction="row" alignItems="center" spacing={0.75} sx={{ fontSize: 12, minWidth: 0 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: "2px", background: e.color, flex: "none" }} />
              <Typography
                sx={{
                  fontFamily: tokens.fontMono,
                  flex: 1,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 12,
                }}
              >
                {ATTACK_CLASS_LABEL[e.k] || e.k}
              </Typography>
              <Typography sx={{ color: tokens.neutral[700], fontSize: 12 }}>{e.v}</Typography>
            </Stack>
          ))}
        </Box>
      </Stack>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tool-call origin grid — wired to GET /mcp/sites/:site_uid/overview
// `geo` payload: [{ country, hits, blocks }]. Pingora writes ISO-2
// into the CH `country` column; empty here when the mmdb lookup
// returned nothing (that row never appears in the rollup).
// ─────────────────────────────────────────────────────────────────────
function geoColours(rate) {
  // rate in 0..1 (fraction of blocks over hits).
  if (rate >= 0.1) {
    return { fg: "#8E1F1F", bg: tokens.danger.bg, border: "#E8B8B8" };
  }
  if (rate >= 0.02) {
    return { fg: "#8A6410", bg: "rgba(242,180,41,0.08)", border: "#F3DC9A" };
  }
  return { fg: "#1E6B3F", bg: "rgba(31,193,107,0.06)", border: "#DBF3B5" };
}

function GeoGrid({ rows, onPick }) {
  const list = Array.isArray(rows) ? rows : [];
  return (
    <Box sx={{ ...sectionCardSx, p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography sx={sectionCardTitleSx}>Tool-call origin</Typography>
      </Stack>
      <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 1.5 }}>
        By GeoIP country, shaded by block rate. Click a cell to filter Events.
      </Typography>
      {list.length === 0 ? (
        <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
          No country-tagged traffic in this window. GeoIP lookups fire on every MCP request
          with a routable client IP.
        </Typography>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(132px, 1fr))", gap: "6px" }}>
          {list.map((c) => {
            const hits = Number(c.hits) || 0;
            const blocks = Number(c.blocks) || 0;
            const ratio = hits > 0 ? blocks / hits : 0;
            const palette = geoColours(ratio);
            return (
              <Box
                key={c.country}
                onClick={() => onPick && onPick(c.country)}
                sx={{
                  borderRadius: "8px", p: 1.25,
                  background: palette.bg, border: `1px solid ${palette.border}`,
                  cursor: "pointer",
                  "&:hover": { borderColor: tokens.primaryIndigo },
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <CountryFlag cc={c.country} showCode />
                  <Typography sx={{ fontSize: 11, fontWeight: 500, color: palette.fg }}>
                    {(ratio * 100).toFixed(ratio >= 0.1 ? 0 : 1)}%
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, color: tokens.neutral[800], mt: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {COUNTRY_NAME[c.country] || c.country}
                </Typography>
                <Typography sx={{ fontSize: 11, color: tokens.neutral[700], fontFamily: tokens.fontMono }}>
                  {hits.toLocaleString()} calls
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────
// SECURITY_COVERAGE_UI_WIRED_2026_11_01
// Compact "Security coverage" panel for the Overview page. Fed by
// GET /mcp/sites/:site_uid/endpoints/:endpoint_id/security_coverage,
// which returns 24 scanners across 6 groups with status/mode/config_field.
// ─────────────────────────────────────────────────────────────────────
// SECURITY_COVERAGE_UI_V2_2026_11_02 — "monitoring" (log-only) is shown
// distinctly from "enforcing" so a detect-only control never reads as
// protection. `active` kept for older backends.
const COVERAGE_STATUS_COLOURS = {
  always_on:  { bg: "#dcfce7", fg: "#166534", label: "Always on" },
  enforcing:  { bg: "#dcfce7", fg: "#166534", label: "Enforcing" },
  monitoring: { bg: "#fef3c7", fg: "#92400e", label: "Log only" },
  active:     { bg: "#dbeafe", fg: "#1e40af", label: "Active" },
  disabled:   { bg: "#f3f4f6", fg: "#6b7280", label: "Off" },
};

function SecurityCoveragePanel({ coverage, coverageErr, endpoints, selectedEpId, onSelectEp, onOpenSettings }) {
  const list = Array.isArray(endpoints) ? endpoints : [];
  if (list.length === 0 || coverageErr) {
    return (
      <Box sx={{ ...sectionCardSx, p: 2.5 }}>
        <Typography sx={sectionCardTitleSx}>Security coverage</Typography>
        <Typography sx={{ fontSize: 13, color: tokens.neutral[700], mt: 1 }}>
          {list.length === 0
            ? "No MCP endpoints on this site yet — add one to see which controls protect it."
            : `Couldn't load coverage: ${coverageErr}`}
        </Typography>
      </Box>
    );
  }
  if (!coverage) {
    return (
      <Box sx={{ ...sectionCardSx, p: 2.5 }}>
        <Typography sx={sectionCardTitleSx}>Security coverage</Typography>
        <Skeleton height={160} />
      </Box>
    );
  }
  const sum = coverage?.summary || {};
  const active = sum.active_count ?? 0;
  const total  = sum.total_count  ?? 0;
  const hasSplit = sum.enforcing_count != null;
  return (
    <Box sx={{ ...sectionCardSx, p: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }}>
        <Typography sx={sectionCardTitleSx}>Security coverage</Typography>
        <Box sx={{ flex: 1 }} />
        {list.length > 0 && (
          <Box sx={{ fontSize: 12, color: tokens.neutral[700] }}>
            <select
              value={selectedEpId || ""}
              onChange={(e) => onSelectEp && onSelectEp(e.target.value)}
              style={{
                border: `1px solid ${tokens.neutral[300]}`,
                background: "#fff",
                padding: "4px 8px",
                borderRadius: 6,
                fontSize: 12,
                fontFamily: "inherit",
                color: tokens.neutral[800],
                minWidth: 220,
              }}
            >
              {list.map((e) => (
                <option key={e.endpoint_id} value={e.endpoint_id}>
                  {e.endpoint_url || e.location_pattern || e.endpoint_id}
                </option>
              ))}
            </select>
          </Box>
        )}
        <Box
          sx={{
            px: 1.25, py: 0.5, borderRadius: "999px",
            fontSize: 12, fontFamily: tokens.fontMono,
            background: tokens.neutral[100], color: tokens.neutral[800],
          }}
        >
          {hasSplit
            ? `${sum.enforcing_count} enforcing · ${sum.monitoring_count} log-only · ${total - active} off`
            : `${active} / ${total} active`}
        </Box>
        {onOpenSettings && selectedEpId && (
          <Typography
            onClick={() => onOpenSettings(selectedEpId)}
            sx={{ fontSize: 12, color: tokens.primaryIndigo || "#4F46E5", cursor: "pointer", whiteSpace: "nowrap" }}
          >
            Security posture →
          </Typography>
        )}
        <Box
          sx={{
            px: 1.25, py: 0.5, borderRadius: "999px",
            fontSize: 11, fontFamily: tokens.fontMono,
            background: coverage.state === "verified" ? "#dcfce7"
              : coverage.state === "stale" ? "#fef3c7" : tokens.neutral[100],
            color: coverage.state === "verified" ? "#166534"
              : coverage.state === "stale" ? "#92400e" : tokens.neutral[700],
          }}
        >
          state: {coverage.state}
        </Box>
      </Stack>

      <Box sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))",
        gap: 2,
      }}>
        {(coverage.groups || []).map((g) => (
          <Box key={g.name} sx={{
            border: `1px solid ${tokens.neutral[200]}`,
            borderRadius: 2, p: 1.5, background: "#fff",
          }}>
            <Typography sx={{
              fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5,
              color: tokens.neutral[600], fontWeight: 600, mb: 1,
            }}>
              {g.label}
            </Typography>
            <Stack spacing={0.75}>
              {(g.items || []).map((it) => {
                const c = COVERAGE_STATUS_COLOURS[it.status] || COVERAGE_STATUS_COLOURS.disabled;
                const dimmed = it.status === "disabled";
                return (
                  <Stack key={it.key} direction="row" alignItems="center" spacing={1}
                    sx={{ fontSize: 12, color: dimmed ? tokens.neutral[600] : tokens.neutral[900] }}
                    title={it.detail || ""}
                  >
                    <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {it.label}
                    </Box>
                    {it.status !== "disabled" && it.mode != null && (
                      <Box sx={{
                        fontFamily: tokens.fontMono, fontSize: 11,
                        color: tokens.neutral[700], px: 0.5,
                      }}>
                        {String(it.mode)}
                      </Box>
                    )}
                    <Box sx={{
                      px: 0.75, py: 0.25, borderRadius: "999px",
                      fontSize: 10, fontWeight: 600, lineHeight: 1.4,
                      background: c.bg, color: c.fg, flex: "none",
                    }}>
                      {c.label}
                    </Box>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

// Main page.
// ─────────────────────────────────────────────────────────────────────
export default function McpOverview() {
  const navigate = useNavigate();
  const { effectiveSiteUid } = useGlobalSite();
  const [timeRange, setTimeRange] = useState("24h");
  const [customDateRange, setCustomDateRange] = useState(["2000-01-01T00:00:00", "2000-01-02T00:00:00"]);
  const [sev, setSev] = useState("all");
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [err, setErr] = useState(null);
  // SECURITY_COVERAGE_UI_WIRED_2026_11_01
  const [coverage, setCoverage] = useState(null);
  const [coverageErr, setCoverageErr] = useState(null);
  const [coverageEpId, setCoverageEpId] = useState(null);

  const hours = useMemo(() => hoursForPeriod(timeRange), [timeRange]);

  useEffect(() => {
    if (!effectiveSiteUid) return;
    let cancelled = false;
    const load = async () => {
      try {
        const [a, b] = await Promise.all([
          axios.get(`mcp/sites/${effectiveSiteUid}/overview?hours=${hours}`),
          axios.get(`mcp/sites/${effectiveSiteUid}/events?hours=${hours}&limit=1000`),
        ]);
        if (cancelled) return;
        setData(a.data);
        setEvents(b.data?.items || []);
        setErr(null);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.message || e.message);
      }
    };
    load();
    const t = setInterval(load, 30 * 1000);
    return () => { cancelled = true; clearInterval(t); };
  }, [effectiveSiteUid, hours]);

  // Auto-select the first endpoint for the coverage panel once data lands,
  // and re-select when the current choice isn't on this site (site switch).
  useEffect(() => {
    const list = Array.isArray(data?.endpoints) ? data.endpoints : [];
    if (coverageEpId && list.some((e) => e.endpoint_id === coverageEpId)) return;
    const next = list[0]?.endpoint_id || null;
    if (next !== coverageEpId) setCoverageEpId(next);
  }, [data, coverageEpId]);

  // Fetch the security coverage grid for the selected endpoint.
  useEffect(() => {
    if (!effectiveSiteUid || !coverageEpId) { setCoverage(null); setCoverageErr(null); return; }
    let cancelled = false;
    setCoverage(null);
    setCoverageErr(null);
    axios
      .get(`mcp/sites/${effectiveSiteUid}/endpoints/${coverageEpId}/security_coverage`)
      .then((r) => { if (!cancelled) setCoverage(r.data); })
      .catch((e) => { if (!cancelled) setCoverageErr(e?.response?.data?.message || e.message || "request failed"); });
    return () => { cancelled = true; };
  }, [effectiveSiteUid, coverageEpId]);

  // Derive rollups from Events.
  const derived = useMemo(() => {
    const toolCounts = {};
    const ipCounts = {};
    const endpointCounts = {};
    const familyCounts = {};
    const agentCounts = {};
    const sessionCounts = {};
    let total = 0;
    let blocks = 0;
    let tags = 0;
    let credential = 0;
    let injection = 0;
    let session = 0;
    for (const r of events || []) {
      total += 1;
      if (r.outcome === "tag") tags += 1;
      else if (r.outcome !== "pass" && r.outcome !== "suppress") blocks += 1;
      if (r.outcome === "credential_leak") credential += 1;
      if (r.outcome === "prompt_injection_blocked") injection += 1;
      if (r.outcome === "session_integrity" || r.outcome === "session_cap") session += 1;
      if (r.tool_name) toolCounts[r.tool_name] = (toolCounts[r.tool_name] || 0) + 1;
      if (r.client_ip) ipCounts[r.client_ip] = (ipCounts[r.client_ip] || 0) + 1;
      if (r.endpoint_id) endpointCounts[r.endpoint_id] = (endpointCounts[r.endpoint_id] || 0) + 1;
      if (r.agent_id) agentCounts[r.agent_id] = (agentCounts[r.agent_id] || 0) + 1;
      if (r.session_fp) sessionCounts[r.session_fp] = (sessionCounts[r.session_fp] || 0) + 1;

      let details = r.details;
      if (typeof details === "string" && details.length > 0) {
        try { details = JSON.parse(details); } catch { details = null; }
      }
      if (details && typeof details === "object") {
        for (const [scanner, slugs] of Object.entries(details)) {
          if (!Array.isArray(slugs)) continue;
          for (const slug of slugs) {
            const fam = attackFamily(scanner, slug);
            familyCounts[fam] = (familyCounts[fam] || 0) + 1;
          }
        }
      }
    }
    const rank = (counts) => Object.entries(counts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, c]) => ({ key: k, label: k, value: c }));
    return {
      total, blocks, tags, credential, injection, session,
      topTools: rank(toolCounts),
      topEndpoints: Object.entries(endpointCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, c]) => ({ key: k, label: `${k.slice(0, 12)}…`, value: c })),
      topIps: rank(ipCounts),
      topAgents: Object.entries(agentCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, c]) => ({ key: k, label: k, value: c })),
      topSessions: Object.entries(sessionCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([k, c]) => ({ key: k, label: k.slice(0, 10), value: c })),
      familyCounts,
      topFamilies: FAMILY_ORDER.filter((k) => familyCounts[k]).slice(0, 6).map((k) => ({ key: k, label: FAMILY_LABEL[k], value: familyCounts[k] })),
    };
  }, [events]);

  // Drift queue — from enriched /overview response. Prefer the server's
  // computed array (which carries pending_count) and fall back to the
  // raw endpoints list for backward compatibility with older CP builds.
  const driftEndpoints = useMemo(() => {
    if (Array.isArray(data?.drift) && data.drift.length) {
      return data.drift.map((d) => ({
        endpoint_id: d.endpoint_id,
        location_pattern: d.location_pattern,
        pending_count: Number(d.pending_count) || 0,
      }));
    }
    return (data?.endpoints || [])
      .filter((e) => e && e.drift_hold)
      .map((e) => ({
        endpoint_id: e.endpoint_id,
        location_pattern: e.location_pattern,
        pending_count: 0,
      }));
  }, [data]);

  // Token-spend rollup — [{ day, endpoint_id, tokens_in, tokens_out }].
  // Collapse by day for the sparkline + sum totals for the headline.
  const tokenSpend = useMemo(() => {
    const perDay = new Map();
    let total = 0;
    for (const r of data?.token_spend || []) {
      const key = r.day;
      if (!key) continue;
      const sum = (Number(r.tokens_in) || 0) + (Number(r.tokens_out) || 0);
      perDay.set(key, (perDay.get(key) || 0) + sum);
      total += sum;
    }
    const days = Array.from(perDay.entries())
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .slice(-14);
    const max = Math.max(1, ...days.map(([, v]) => v));
    return { days, max, total };
  }, [data]);

  // Session-anomaly counts — authoritative server rollup over the CH
  // details column. Shape: { recon_then_write, narrow_toolmix, burst_write }.
  const anomalyCards = useMemo(() => {
    const a = data?.anomalies || {};
    return [
      {
        slug: "mcp.anomaly.recon_then_write",
        desc: "Discovery (list/search/describe) calls directly before a write-class call on the same session.",
        count: Number(a.recon_then_write || 0),
      },
      {
        slug: "mcp.anomaly.narrow_toolmix",
        desc: "A session stuck on one or two tools over its last six calls — classic script pattern.",
        count: Number(a.narrow_toolmix || 0),
      },
      {
        slug: "mcp.anomaly.burst_write",
        desc: "Several write-class calls in rapid succession on the same session.",
        count: Number(a.burst_write || 0),
      },
    ];
  }, [data]);

  const loading = effectiveSiteUid && (data == null || events == null);

  const openEvents = (key, val) =>
    navigate(`/v2/mcp/events?${key}=${encodeURIComponent(val)}`);

  const score = data?.risk?.score;

  return (
    <>
      <Helmet title="MCP Security — Overview" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Overview"
          sx={{ pb: 0 }}
          actions={(
            <GreenTimeRangePicker
              selectPeriod={setTimeRange}
              selectCustomDateRange={setCustomDateRange}
              timeRange={timeRange}
              customDateRange={customDateRange}
            />
          )}
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {err && <Alert severity="error">{err}</Alert>}
              {!effectiveSiteUid && <Alert severity="info">Select a site from the header to see MCP overview.</Alert>}

              {/* ── Endpoint filter + severity tabs ── */}
              <Stack direction="row" alignItems="center" spacing={1.5} flexWrap="wrap">
                <Box
                  sx={{
                    display: "flex", alignItems: "center", gap: 1,
                    height: 36, px: 1.5,
                    border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px",
                    background: "#fff", fontSize: 13,
                  }}
                >
                  <span style={{ color: tokens.neutral[700] }}>Endpoint</span>
                  <span>All endpoints ({data?.endpoints_count ?? 0})</span>
                </Box>
                <Box sx={{ display: "flex", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                  {SEV_OPTS.map((s) => (
                    <Box
                      key={s.k}
                      onClick={() => setSev(s.k)}
                      sx={{
                        height: 34, px: 1.75, display: "grid", placeItems: "center",
                        fontSize: 13, cursor: "pointer",
                        background: sev === s.k ? tokens.primaryIndigo : "#fff",
                        color: sev === s.k ? "#fff" : tokens.neutral[800],
                      }}
                    >
                      {s.label}
                    </Box>
                  ))}
                </Box>
              </Stack>

              {/* ── 8-card metric strip ── */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 1.5 }}>
                <MetricCard
                  label="Risk score"
                  value={score == null ? "—" : score}
                  sub={score == null ? "0–100, refreshed every 5 min" : `Updated ${new Date(data.risk.scored_at).toLocaleTimeString()}`}
                />
                <MetricCard
                  label="Decisions"
                  value={derived.total.toLocaleString()}
                  sub={`${hours}h window`}
                />
                <MetricCard
                  label="Blocks + holds"
                  value={derived.blocks.toLocaleString()}
                  sub={derived.total ? `${((derived.blocks * 100) / derived.total).toFixed(1)}% of decisions` : "no traffic"}
                />
                <MetricCard
                  label="Tagged"
                  value={derived.tags.toLocaleString()}
                  sub={`log-only matches`}
                />
                <MetricCard
                  label="Injection hits"
                  value={derived.injection.toLocaleString()}
                  sub="on tool output"
                />
                <MetricCard
                  label="Credential findings"
                  value={derived.credential.toLocaleString()}
                  sub="on tool output"
                />
                <MetricCard
                  label="Session failures"
                  value={derived.session.toLocaleString()}
                  sub="integrity + cap"
                />
                <MetricCard
                  label="Endpoints"
                  value={(data?.endpoints_count ?? 0).toLocaleString()}
                  sub="live on this site"
                />
              </Box>

              {/* ── Security coverage ── */}
              <SecurityCoveragePanel
                coverage={coverage}
                coverageErr={coverageErr}
                onOpenSettings={(id) => navigate(`/v2/mcp/settings/${id}`)}
                endpoints={data?.endpoints || []}
                selectedEpId={coverageEpId}
                onSelectEp={setCoverageEpId}
              />

              {/* ── Hourly chart ── */}
              {loading ? <Skeleton height={300} /> : <HourlyChart events={events} />}

              {/* ── GeoIP + Attack-class mix ── */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))", gap: 2 }}>
                <GeoGrid
                  rows={data?.geo}
                  onPick={(cc) => navigate(`/v2/mcp/events?country=${encodeURIComponent(cc)}`)}
                />
                <AttackMixDonut familyCounts={derived.familyCounts} />
              </Box>

              {/* ── 6-grid Top-N panels ── */}
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))", gap: 2 }}>
                <TopPanel
                  title="Top tools"
                  loading={loading}
                  rows={derived.topTools.map((r) => ({ ...r, onClick: () => openEvents("tool", r.key) }))}
                  onViewAll={() => navigate("/v2/mcp/tools")}
                />
                <TopPanel
                  title="Top endpoints"
                  loading={loading}
                  rows={derived.topEndpoints.map((r) => ({ ...r, onClick: () => openEvents("endpoint_id", r.key) }))}
                />
                <TopPanel
                  title="Top client IPs"
                  loading={loading}
                  rows={derived.topIps.map((r) => ({ ...r, onClick: () => openEvents("client_ip", r.key) }))}
                />
                <TopPanel title="Top attack classes" loading={loading} rows={derived.topFamilies} />
                <TopPanel
                  title="Top agents"
                  isNew
                  loading={loading}
                  rows={derived.topAgents}
                  onViewAll={() => navigate("/v2/mcp/agents")}
                />
                <TopPanel
                  title="Top sessions"
                  isNew
                  loading={loading}
                  rows={derived.topSessions.map((r) => ({ ...r, label: r.label }))}
                  onViewAll={() => navigate("/v2/mcp/sessions")}
                />
              </Box>

              {/* ── MCP-only signals strip ── */}
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography
                  sx={{
                    fontFamily: tokens.fontDisplay,
                    fontWeight: 700, fontSize: 12,
                    letterSpacing: "0.06em", textTransform: "uppercase",
                    color: tokens.neutral[800],
                  }}
                >
                  MCP-only signals
                </Typography>
                <Box sx={{ flex: 1, height: 1, background: tokens.neutral[300] }} />
              </Stack>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 1.5 }}>
                {anomalyCards.map((an) => (
                  <Box
                    key={an.slug}
                    sx={{
                      ...sectionCardSx,
                      display: "flex", flexDirection: "column", gap: 1,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <code style={{ fontFamily: tokens.fontMono, fontSize: 13, fontWeight: 500, flex: 1 }}>{an.slug}</code>
                      <Typography sx={{ fontSize: 11, color: tokens.neutral[700] }}>{hours} h</Typography>
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>{an.desc}</Typography>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                      <Typography sx={{ fontSize: 26, fontWeight: 600, flex: 1 }}>{an.count}</Typography>
                      {an.count > 0 && (
                        <Typography
                          sx={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.primary[300], cursor: "pointer" }}
                          onClick={() => navigate(`/v2/mcp/events?class=${encodeURIComponent(an.slug.split(".").pop())}`)}
                        >
                          view
                        </Typography>
                      )}
                    </Stack>
                  </Box>
                ))}
              </Box>

              {/* ── Drift queue ── */}
              <Box sx={sectionCardSx}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: driftEndpoints.length ? 1.5 : 0 }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={sectionCardTitleSx}>Drift queue</Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                      Endpoints in drift_hold with tools awaiting approval.
                    </Typography>
                  </Box>
                  {driftEndpoints.length > 0 && (
                    <Button sx={primaryButtonStyle}>Approve all</Button>
                  )}
                </Stack>
                {driftEndpoints.length === 0 ? (
                  <Typography sx={{ fontSize: 13, color: tokens.neutral[700] }}>
                    No endpoints in drift_hold.
                  </Typography>
                ) : (
                  <Stack direction="row" spacing={1.25} sx={{ overflowX: "auto", pb: 0.5 }}>
                    {driftEndpoints.map((d) => (
                      <Box
                        key={d.endpoint_id}
                        onClick={() => navigate(`/v2/mcp/tools?endpoint_id=${encodeURIComponent(d.endpoint_id)}`)}
                        sx={{
                          flex: "none", minWidth: 280,
                          border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", p: 1.5,
                          background: tokens.neutral[50], cursor: "pointer",
                          "&:hover": { borderColor: tokens.primaryIndigo, background: tokens.primary.alpha },
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <code style={{ fontFamily: tokens.fontMono, fontSize: 13, flex: 1 }}>
                            {d.location_pattern || d.endpoint_id?.slice(0, 10)}
                          </code>
                          <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: tokens.warning.bg, color: tokens.secondary[500] }}>drift_hold</span>
                        </Stack>
                        <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mt: 0.5 }}>
                          {d.pending_count} tool{d.pending_count === 1 ? "" : "s"} awaiting approval
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>

              {/* ── Token spend ── */}
              <Box sx={{ ...sectionCardSx, p: 2.5 }}>
                <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap" sx={{ mb: 1.5 }}>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={sectionCardTitleSx}>Token spend</Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                      Daily tokens_in + tokens_out across endpoints, last 14 days. Shown where the upstream returns a
                      <code style={{ fontFamily: tokens.fontMono, fontSize: 11, padding: "0 4px" }}>result.usage</code>
                      payload.
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "right" }}>
                    <Typography sx={{ fontSize: 22, fontWeight: 600 }}>
                      {tokenSpend.total >= 1_000_000
                        ? `${(tokenSpend.total / 1_000_000).toFixed(1)}M`
                        : tokenSpend.total >= 1_000
                          ? `${(tokenSpend.total / 1_000).toFixed(1)}K`
                          : tokenSpend.total.toLocaleString()}
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>tokens in window</Typography>
                  </Box>
                </Stack>
                {tokenSpend.days.length === 0 ? (
                  <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                    No MCP response in this window advertised a <code>result.usage</code> envelope.
                  </Typography>
                ) : (
                  <Box sx={{ height: 110, display: "flex", alignItems: "flex-end", gap: "6px", borderBottom: `1px solid ${tokens.neutral[300]}` }}>
                    {tokenSpend.days.map(([day, v]) => {
                      const h = Math.max(2, Math.round((v / tokenSpend.max) * 100));
                      return (
                        <Box key={day} sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column-reverse" }} title={`${day}: ${v.toLocaleString()} tokens`}>
                          <Box sx={{ height: `${h}%`, background: tokens.primaryIndigo, opacity: 0.6, borderTop: "1px solid #fff" }} />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>
    </>
  );
}
